import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { colors } from "../../../ui/colors";
import { createPortal } from "react-dom";
import { useState } from "react";
import { MinimizedModalTab } from "./MinimizedModalTab";

type MinimizedModal = {
  id: string | number;
  key?: string;
  title?: string;
  label?: string;
  minimized?: boolean;
};

type MinimizedModalsTabsViewProps = {
  minimizedModals: MinimizedModal[];
  getLabel: (modal: MinimizedModal) => string;
  onRestore: (modal: MinimizedModal) => void;
  onClose: (modal: MinimizedModal) => void;
  onReorder: (sourceId: MinimizedModal["id"], targetId: MinimizedModal["id"]) => void;
  leftOffset: number;
};

export const MinimizedModalsTabsView = ({
  minimizedModals,
  getLabel,
  onRestore,
  onClose,
  onReorder,
  leftOffset,
}: MinimizedModalsTabsViewProps) => {
  const [isStackedMode, setIsStackedMode] = useState(true);
  const [draggedId, setDraggedId] = useState<MinimizedModal["id"] | null>(null);
  const [dragOverId, setDragOverId] = useState<MinimizedModal["id"] | null>(null);

  if (minimizedModals.length === 0) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        top: { xs: 54, md: 50 },
        left: { xs: 10, md: leftOffset },
        right: { xs: 10, md: 24 },
        zIndex: 29990,
        pointerEvents: "none",
        overflow: "visible",
        pt: 0.5,
        pb: 0.75,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          overflowX: "visible",
          overflowY: "visible",
          pointerEvents: "auto",
          pb: 0.25,
          "& .minimized-modal-tab-active .minimized-modal-tab-label": {
            opacity: 1,
          },
          "& .minimized-modal-tab-active .minimized-modal-tab-close": {
            opacity: 1,
            pointerEvents: "auto",
          },
          "&::-webkit-scrollbar": {
            height: 5,
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Tooltip title={isStackedMode ? "Click to keep tabs expanded" : "Click to stack tabs again"} arrow>
          <IconButton
            size="small"
            onClick={() => setIsStackedMode((current) => !current)}
            aria-label={isStackedMode ? "Click to keep tabs expanded" : "Click to stack tabs again"}
            sx={{
              width: 24,
              height: 24,
              flex: "0 0 auto",
              p: 0,
              color: isStackedMode ? colors.gray[500] || colors.gray.base : colors.orange.base,
              bgcolor: isStackedMode ? "transparent" : "#FFFFFF",
              border: isStackedMode ? "1px solid transparent" : `1px solid ${colors.orange[200]}`,
              boxShadow: isStackedMode ? "none" : "0 2px 6px rgba(0,0,0,0.06)",
              "&:hover": {
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                borderColor: colors.orange[200],
              },
            }}
          >
            <LayersOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: "1 1 auto",
            minWidth: 0,
            overflowX: "auto",
            overflowY: "visible",
            scrollbarWidth: "thin",
            ...(isStackedMode && {
              "&:hover .minimized-modal-tab": {
                marginLeft: 0,
                opacity: 1,
              },
              "&:hover .minimized-modal-tab-card": {
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              },
              "&:hover .minimized-modal-tab-label": {
                opacity: 1,
              },
              "&:hover .minimized-modal-tab-close": {
                opacity: 1,
                pointerEvents: "auto",
              },
            }),
            "&::-webkit-scrollbar": {
              height: 5,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 8,
              backgroundColor: "rgba(15, 23, 42, 0.18)",
            },
          }}
        >
          {minimizedModals.map((modal, index) => (
            <Box
              key={modal.id}
              className={`minimized-modal-tab ${index === minimizedModals.length - 1 ? "minimized-modal-tab-active" : ""}`}
              draggable
              onDragStart={(event) => {
                setDraggedId(modal.id);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(modal.id));
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDragOverId(modal.id);
              }}
              onDragLeave={() => {
                setDragOverId((current) => (current === modal.id ? null : current));
              }}
              onDrop={(event) => {
                event.preventDefault();
                const sourceId = draggedId ?? event.dataTransfer.getData("text/plain");
                setDraggedId(null);
                setDragOverId(null);

                if (sourceId && String(sourceId) !== String(modal.id)) {
                  onReorder(sourceId, modal.id);
                }
              }}
              onDragEnd={() => {
                setDraggedId(null);
                setDragOverId(null);
              }}
              sx={{
                flex: "0 0 auto",
                ml: isStackedMode ? (index === 0 ? 0.5 : "-104px") : index === 0 ? 0.5 : 0.75,
                opacity: !isStackedMode || index === 0 || index === minimizedModals.length - 1 ? 1 : 0.86,
                transition: "margin-left 180ms ease, opacity 140ms ease, transform 140ms ease",
                transform: dragOverId === modal.id && draggedId !== modal.id ? "translateY(-2px)" : "none",
                zIndex: index + 1,
              }}
            >
              <MinimizedModalTab
                label={getLabel(modal)}
                modalKey={modal.key}
                stacked={isStackedMode && index > 0}
                showClose={!isStackedMode || index === minimizedModals.length - 1}
                mutedLabel={isStackedMode && index !== minimizedModals.length - 1}
                onRestore={() => onRestore(modal)}
                onClose={() => onClose(modal)}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>,
    document.body,
  );
};
